// /mnt/mantenimientos/frontend/src/components/InfraestructuraTab.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import ModuleHeader from "./ui/ModuleHeader";

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pct(v) {
  const n = toNum(v);
  return n == null ? "—" : `${n.toFixed(1)}%`;
}

function fdateClock(unix) {
  const t = toNum(unix);
  if (!t || t <= 0) return "—";
  try {
    return new Date(t * 1000).toLocaleString("es-ES");
  } catch {
    return String(t);
  }
}

function fmtGB(v) {
  const n = toNum(v);
  if (n == null) return "—";
  return `${n.toFixed(n >= 10 ? 0 : 1)} GB`;
}

// etiquetas bonitas para os_class del backend
const OS_LABEL = {
  ws2025: "Win Server 2025",
  ws2022: "Win Server 2022",
  ws2019: "Win Server 2019",
  ws2016: "Win Server 2016",
  ws2012: "Win Server 2012",
  ws: "Windows Server",
  w11: "Win11",
  w10: "Win10",
  w8: "Win8",
  w7: "Win7",
  linux: "Linux",
  mac: "macOS",
  other: "Otros",
};

export default function InfraestructuraTab() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // datos
  const [data, setData] = useState({ total: 0, paging: { page: 1, page_size: 50, pages: 1 }, kpis: null, equipos: [] });
  const equipos = useMemo(
    () => (Array.isArray(data.equipos) ? data.equipos : []),
    [data.equipos]
  );

  // filtros UI
  const [q, setQ] = useState("");
  const [clienteQ, setClienteQ] = useState("");
  const [diskFreeLt, setDiskFreeLt] = useState(""); // numero (%)
  const [agentOk, setAgentOk] = useState("all"); // all | ok | ko
  const [onlyWithProblems, setOnlyWithProblems] = useState(false);
  const [osChip, setOsChip] = useState("all"); // all | w10 | ws2022 ...
  const [osLegacy, setOsLegacy] = useState(false); // Win10 o inferior
  const [staleMin, setStaleMin] = useState(""); // minutos sin actualizar (>=)

  // paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // modal
  const [modalRow, setModalRow] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState("");

  const buildQueryParams = useCallback(
    ({ includeDisks = false } = {}) => {
      const params = {
        page,
        page_size: pageSize,
      };

      // “buscar” local: lo convertimos en filtros de backend lo más posible
      // (q la aplicamos cliente/so “contiene” por backend solo en campos existentes)
      // Para algo súper global, q se puede mapear a so+cliente, y el resto lo filtras visualmente.
      if (clienteQ.trim()) params.cliente = clienteQ.trim();
      if (q.trim()) params.so = q.trim(); // útil para “Windows”, “Ubuntu”, etc.

      if (osChip !== "all") params.os_class = osChip;
      if (osLegacy) params.os_legacy = 1;

      if (diskFreeLt !== "") {
        const n = toNum(diskFreeLt);
        if (n != null) params.disk_free_lt = n;
      }

      if (staleMin !== "") {
        const n = toNum(staleMin);
        if (n != null) params.stale_min = n;
      }

      if (agentOk === "ok") params.agent_ok = 1;
      if (agentOk === "ko") params.agent_ok = 0;

      if (onlyWithProblems) params.problems_gt = 0;

      if (includeDisks) params.include_disks = 1;

      return params;
    },
    [page, pageSize, q, clienteQ, diskFreeLt, agentOk, onlyWithProblems, osChip, osLegacy, staleMin]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/api/monitorizacion/zabbix/equipos", {
        params: buildQueryParams({ includeDisks: false }),
      });
      setData(res.data || {});
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "No se pudo cargar el listado global de infraestructura.");
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  // cargar al entrar + cuando cambian filtros/paginación
  useEffect(() => {
    load();
  }, [load]);

  // KPIs arriba (del backend si vienen)
  const kpis = data?.kpis || {
    problems: equipos.filter((e) => (toNum(e.problems_total) || 0) > 0).length,
    agent_ko: equipos.filter((e) => e.agent_ok === false).length,
    disk_crit: equipos.filter((e) => toNum(e.worst_disk_free_pct) != null && toNum(e.worst_disk_free_pct) < 20).length,
    stale_60m: 0,
  };

  // chips OS (contando solo la página actual; suficiente para UX)
  const osChips = useMemo(() => {
    const m = new Map();
    for (const r of equipos) {
      const key = String(r.os_class || "other");
      m.set(key, (m.get(key) || 0) + 1);
    }
    const order = ["ws2025","ws2022","ws2019","ws2016","ws2012","ws","w11","w10","w8","w7","linux","mac","other"];
    return Array.from(m.entries())
      .map(([key, count]) => ({ key, label: OS_LABEL[key] || key, count }))
      .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }, [equipos]);

  const openModal = useCallback(async (row) => {
    if (!row?.hostid) return;
    setModalRow(null);
    setModalError("");
    setModalLoading(true);
    try {
      // pedimos la misma fila pero con discos incluidos.
      // Para no depender de un endpoint “detalle”, usamos include_disks=1 + filtro por hostid “a posteriori”.
      // (Si luego hacemos endpoint detalle, esto se simplifica muchísimo.)
      const res = await axios.get("/api/monitorizacion/zabbix/equipos", {
        params: { ...buildQueryParams({ includeDisks: true }), page: 1, page_size: 200, include_disks: 1 },
      });

      const arr = Array.isArray(res.data?.equipos) ? res.data.equipos : [];
      const found = arr.find((x) => String(x.hostid) === String(row.hostid)) || row;

      setModalRow(found);
    } catch (e) {
      console.error(e);
      setModalError(e?.response?.data?.error || "No se pudo cargar el detalle del equipo.");
      setModalRow(row); // al menos mostramos lo básico
    } finally {
      setModalLoading(false);
    }
  }, [buildQueryParams]);

  const closeModal = () => setModalRow(null);

  // ESC para cerrar + bloquear scroll
  useEffect(() => {
    if (!modalRow) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [modalRow]);

  const paging = data?.paging || { page, page_size: pageSize, pages: 1 };

  return (
    <div className="infra-page">
      <ModuleHeader eyebrow="Administración" title="Estado del sistema" description="Vista global temporal basada en la monitorización heredada." actions={<button className="button secondary" onClick={load} disabled={loading}>{loading ? "Cargando…" : "Actualizar"}</button>} />
    <section className="card">
      <div className="clientes-header mp-panel-heading" style={{ alignItems: "flex-start" }}>
        <div>
          <h3 style={{ margin: 0 }}>Infraestructura heredada</h3>
          <div className="placeholder-text" style={{ marginTop: 6 }}>
            Consulta temporal durante la retirada progresiva de Zabbix.
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button className="button secondary" onClick={load} disabled={loading} title="Recargar">
            {loading ? "⏳ Cargando…" : "🔄 Recargar"}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "12px 0" }}>
        <span className="pill pill-neutral">Total (filtro): {data?.total ?? equipos.length}</span>
        <span className="pill pill-neutral">Con incidencias: {kpis.problems ?? 0}</span>
        <span className={`pill ${(kpis.agent_ko ?? 0) ? "pill-warn" : "pill-ok"}`}>Agente KO: {kpis.agent_ko ?? 0}</span>
        <span className={`pill ${(kpis.disk_crit ?? 0) ? "pill-warn" : "pill-ok"}`}>Disco &lt; 20%: {kpis.disk_crit ?? 0}</span>
        {kpis.stale_60m != null && <span className="pill pill-neutral">Stale ≥ 60m: {kpis.stale_60m}</span>}
      </div>

      {/* Chips OS */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <button
          type="button"
          className={`pill ${osChip === "all" ? "pill-ok" : "pill-neutral"}`}
          onClick={() => {
            setOsChip("all");
            setPage(1);
          }}
        >
          Todos
        </button>

        {osChips.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`pill ${osChip === c.key ? "pill-ok" : "pill-neutral"}`}
            onClick={() => {
              setOsChip(c.key);
              setPage(1);
            }}
            title={`Filtrar por ${c.label}`}
          >
            {c.label}: {c.count}
          </button>
        ))}

        <button
          type="button"
          className={`pill ${osLegacy ? "pill-warn" : "pill-neutral"}`}
          onClick={() => {
            setOsLegacy((v) => !v);
            setPage(1);
          }}
          title="Windows 10 o inferior"
        >
          Win10 o inferior
        </button>
      </div>

      {/* Filtros */}
      <div className="filtros" style={{ marginTop: 0 }}>
        <input
          className="buscador"
          style={{ marginBottom: 0, maxWidth: 320 }}
          placeholder='Buscar OS (ej: "Windows", "Ubuntu")…'
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
        />

        <input
          type="text"
          placeholder='Cliente (código) contiene… (ej: "123")'
          value={clienteQ}
          onChange={(e) => {
            setClienteQ(e.target.value);
            setPage(1);
          }}
          style={{ padding: 6, borderRadius: 6, border: "1px solid #ccc", minWidth: 240 }}
        />

        <input
          type="number"
          min="0"
          max="100"
          placeholder="Disco libre < % (ej: 20)"
          value={diskFreeLt}
          onChange={(e) => {
            setDiskFreeLt(e.target.value);
            setPage(1);
          }}
          style={{ padding: 6, borderRadius: 6, border: "1px solid #ccc", width: 190 }}
        />

        <input
          type="number"
          min="0"
          placeholder="Stale min (>=) (ej: 120)"
          value={staleMin}
          onChange={(e) => {
            setStaleMin(e.target.value);
            setPage(1);
          }}
          style={{ padding: 6, borderRadius: 6, border: "1px solid #ccc", width: 190 }}
        />

        <select
          className="input"
          style={{ width: 190 }}
          value={agentOk}
          onChange={(e) => {
            setAgentOk(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">Agente: todos</option>
          <option value="ok">Agente: OK</option>
          <option value="ko">Agente: KO</option>
        </select>

        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={onlyWithProblems}
            onChange={(e) => {
              setOnlyWithProblems(e.target.checked);
              setPage(1);
            }}
          />
          Solo con incidencias
        </label>

        <select
          className="input"
          style={{ width: 150 }}
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value || 50));
            setPage(1);
          }}
          title="Tamaño de página"
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
        </select>
      </div>

      {error && (
        <div className="placeholder-text" style={{ color: "#ff7676" }}>
          {error}
        </div>
      )}

      {loading ? (
        <p className="placeholder-text">Cargando listado global…</p>
      ) : (
        <>
          {equipos.length === 0 ? (
            <p className="placeholder-text">No hay equipos que coincidan con los filtros.</p>
          ) : (
            <table className="tabla-informes infra-table-click">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>PC</th>
                  <th>Host</th>
                  <th>OS</th>
                  <th>Libre%</th>
                  <th>Agente</th>
                  <th>Incidencias</th>
                  <th>Actualizado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {equipos.map((r) => {
                  const free = toNum(r.worst_disk_free_pct);
                  const freeTxt = free == null ? "—" : `${free.toFixed(1)}%`;
                  const agentOkRow = !!r.agent_ok;
                  const incid = toNum(r.problems_total) || 0;

                  return (
                    <tr key={`${r.pc_id}-${r.hostid}`}>
                      <td>
                        <span className="cellMono">{r.cliente || "—"}</span>
                      </td>

                      <td style={{ fontWeight: 800 }}>{r.pc_id || "—"}</td>
                      <td>{r.host || "—"}</td>

                      <td title={r.os || ""}>
                        <span className="cellTag">{OS_LABEL[r.os_class] || r.os_class || "—"}</span>
                      </td>

                      <td style={{ fontWeight: 900 }}>
                        {free != null && free < 15 ? "🔴 " : free != null && free < 30 ? "🟠 " : ""}
                        {freeTxt}
                      </td>

                      <td>
                        <span className={`pill ${agentOkRow ? "pill-ok" : "pill-ko"}`}>
                          {agentOkRow ? "🟢 OK" : "🔴 KO"}
                        </span>
                      </td>

                      <td style={{ fontWeight: 900 }}>{incid}</td>
                      <td>{fdateClock(r.lastclock)}</td>

                      <td>
                        <button className="button secondary" type="button" onClick={() => openModal(r)}>
                          Ver
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Paginación */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, gap: 10, flexWrap: "wrap" }}>
            <div className="placeholder-text" style={{ margin: 0 }}>
              Página {paging.page} / {paging.pages} · Total: {data?.total ?? 0}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="button secondary"
                type="button"
                disabled={paging.page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ◀ Anterior
              </button>
              <button
                className="button secondary"
                type="button"
                disabled={paging.page >= paging.pages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente ▶
              </button>
            </div>
          </div>
        </>
      )}

      {/* MODAL DETALLE */}
      {(modalLoading || modalRow) && (
        <div className="infra-modal-overlay" onClick={closeModal} role="dialog" aria-modal="true">
          <div className="infra-modal" onClick={(e) => e.stopPropagation()}>
            <div className="infra-modal-head">
              <div className="infra-modal-title">
                <div className="t">
                  🖥️ {modalRow?.pc_id || "PC"} — {modalRow?.host || "Host"}
                </div>
                <div className="s">
                  Cliente: {modalRow?.cliente || "—"} · {OS_LABEL[modalRow?.os_class] || modalRow?.os_class || ""} · HostID:{" "}
                  {modalRow?.hostid || "—"} · Última actualización: {fdateClock(modalRow?.lastclock)}
                </div>
              </div>
              <button className="infra-modal-close" onClick={closeModal} aria-label="Cerrar">
                ✕
              </button>
            </div>

            <div className="infra-modal-body">
              {modalLoading && <div className="placeholder-text">Cargando detalle…</div>}
              {modalError && <div className="placeholder-text" style={{ color: "#ff7676" }}>{modalError}</div>}

              {modalRow && (
                <>
                  <div className="infra-modal-grid">
                    <div className="infra-kv">
                      <div className="k">CPU</div>
                      <div className="v">{pct(modalRow.cpu_pct)}</div>
                    </div>
                    <div className="infra-kv">
                      <div className="k">RAM</div>
                      <div className="v">
                        {pct(modalRow.ram_pct)}
                        {modalRow.ram_total_gb != null ? ` · ${fmtGB(modalRow.ram_total_gb)}` : ""}
                      </div>
                    </div>
                    <div className="infra-kv">
                      <div className="k">Agente</div>
                      <div className="v">{modalRow.agent_ok ? "🟢 OK" : "🔴 KO"}</div>
                    </div>
                    <div className="infra-kv">
                      <div className="k">Incidencias</div>
                      <div className="v">{modalRow.problems_total || 0}</div>
                    </div>
                    <div className="infra-kv">
                      <div className="k">Disco (peor)</div>
                      <div className="v">
                        {modalRow.worst_disk_free_pct != null ? `${Number(modalRow.worst_disk_free_pct).toFixed(1)}% libre` : "—"}
                      </div>
                    </div>
                  </div>

                  {/* DISCS (solo si include_disks devolvió discos) */}
                  <div className="infra-modal-section" style={{ marginTop: 14 }}>
                    <div className="infra-modal-section-title">💽 Discos</div>

                    {!Array.isArray(modalRow.disks) || modalRow.disks.length === 0 ? (
                      <div className="placeholder-text">Sin detalle de discos (no solicitado o no disponible).</div>
                    ) : (
                      <div className="infra-modal-tablewrap">
                        <table className="tabla-informes">
                          <thead>
                            <tr>
                              <th>Unidad</th>
                              <th>Usado%</th>
                              <th>Libre%</th>
                              <th>Tamaño</th>
                              <th>Libre (GB)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modalRow.disks.map((d, idx) => {
                              const used = toNum(d?.used_pct);
                              const free = used == null ? null : Math.max(0, Math.min(100, 100 - used));
                              return (
                                <tr key={idx}>
                                  <td><strong>{d?.drive || "—"}</strong></td>
                                  <td>{pct(used)}</td>
                                  <td style={{ fontWeight: 900 }}>
                                    {free != null && free <= 15 ? "🔴 " : free != null && free <= 30 ? "🟠 " : ""}
                                    {free == null ? "—" : `${free.toFixed(1)}%`}
                                  </td>
                                  <td>{d?.total_gb != null ? fmtGB(d.total_gb) : "—"}</td>
                                  <td>{d?.free_gb != null ? fmtGB(d.free_gb) : "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="infra-modal-foot">
              <button className="button" onClick={closeModal}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
    </div>
  );
}
